import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FiHelpCircle, FiCheck, FiClock } from 'react-icons/fi';
import { InAppReview } from '@capacitor-community/in-app-review';
import { Capacitor } from '@capacitor/core';
import posthog from 'posthog-js';
import { getFormattedPrices } from '../../utils/configUtils';
import OrzelZpucharemIcon from '../../img/icons/orzel_ksiazka.png';
import OrzelZLodemIcon from '../../img/icons/orzel_kostka.png';
import OrzelGwiazdyIcon from '../../img/icons/orzel_puchar.png';

// ============================================================
// A/B test: 2 warianty standardowego CTA (streak < 10).
// Deterministyczny przydział per user (hash distinct_id).
// ============================================================
const STANDARD_REVIEW_VARIANTS = ['direct', 'stars', 'stars_anon'];

const getStableVariantIndex = (variantCount) => {
    const distinctId = posthog.get_distinct_id?.();
    if (!distinctId) return 0; // fallback do pierwszego wariantu ('direct')
    let hash = 0;
    for (let i = 0; i < distinctId.length; i++) {
        hash = ((hash << 5) - hash + distinctId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % variantCount;
};

// ============================================================
// InAppReview — wywoływane PRZED onClose(), nie po.
// Capacitor bridge działa niezależnie od lifecycle Reacta,
// więc dialog inicjalizuje się gdy komponent jest jeszcze
// zamontowany, a potem żyje własnym życiem niezależnie od
// unmount. Unikamy problemu "dialog podłączony do znikającego
// komponentu".
// ============================================================
const triggerInAppReview = () => {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
        return InAppReview.requestReview();
    }
    return Promise.resolve();
};

// Złoty pasek celu zapełniający się 0→100% przy otwarciu popupu.
// Dla completionLevel > 1 pokazuje ×N (podwójny/potrójny cel).
const GoalFillBar = ({ completionLevel = 1, reduced, solved, goal }) => {
    const overachieved = completionLevel > 1;
    const hasCounts = goal > 0 && solved != null;
    return (
        <div className="mb-5 px-1">
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Cel dzienny</span>
                <motion.span
                    initial={reduced ? false : { scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: reduced ? 0 : 1.0, type: 'spring', stiffness: 400, damping: 14 }}
                    className="flex items-center gap-1 text-xs font-bold text-[#E09F2E]"
                >
                    {hasCounts
                        ? (<><FiCheck size={12} strokeWidth={3} /> {solved}/{goal} zadań</>)
                        : overachieved ? `×${completionLevel}` : (<><FiCheck size={12} strokeWidth={3} /> Zaliczony!</>)}
                </motion.span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner overflow-hidden">
                <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-yellow-300 to-[#F8AB3B]"
                    initial={reduced ? { width: '100%' } : { width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 0.9, ease: 'easeOut', delay: 0.25 }}
                />
            </div>
        </div>
    );
};

const FreezeInfoTooltip = () => (
    <div className="absolute z-20 bottom-full right-0 mb-2 w-72 rounded-lg bg-[#233855] p-4 text-sm text-left text-white shadow-lg space-y-3">
        <p className="font-bold text-base">Jak działają Zamrożenia Sesji ❄️?</p>
        <p>Są zużywane <b>automatycznie</b>, gdy opuścisz dzień nauki. Nie musisz nic robić – Twoja seria 🔥 jest bezpieczna.</p>
        <div>
            <p className="font-semibold">Jak je zdobyć?</p>
            <ul className="list-disc list-inside text-xs mt-1 space-y-1">
                <li>Otrzymasz <b>1 zamrożenie</b> za każdym razem, gdy zrealizujesz 2x lub 3x swój cel dzienny lub w prezencie za osiągnięcie kolejnego poziomu odznaki.</li>
            </ul>
        </div>
    </div>
);

const DailyGoalSuccessPopup = ({
    completionLevel,
    onClose,
    isFirstSession = false,
    shouldRequestReview: reviewRequested = false,
    streakDay,
    welcomeOfferExpiresAt = null,
    welcomeOfferContext = null,
    onCheckOffer,
    goalSolved = null,
    goalTotal = null
}) => {
    // Na webie nie ma sklepu do oceny — prośba byłaby martwym przyciskiem.
    const shouldRequestReview = reviewRequested && Capacitor.isNativePlatform();
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);
    const popupOpenedAtRef = useRef(Date.now());
    const reviewFiredRef = useRef(false);
    const reduced = useReducedMotion();

    // Stabilny wariant A/B per user — nie zmienia się przez cały lifecycle komponentu
    const standardReviewVariant = useMemo(
        () => STANDARD_REVIEW_VARIANTS[getStableVariantIndex(STANDARD_REVIEW_VARIANTS.length)],
        []
    );

    // --- ANALITYKA ---
    useEffect(() => {
        posthog.capture('daily_goal_popup_viewed', {
            completion_level: completionLevel,
            should_request_review: shouldRequestReview,
            platform: Capacitor.getPlatform(),
            review_variant: streakDay >= 10 ? 'veteran' : standardReviewVariant,
            streak_day: streakDay,
            is_first_session: isFirstSession,
            welcome_offer_granted: !!welcomeOfferExpiresAt,
        });
    }, [completionLevel, shouldRequestReview, streakDay, isFirstSession, standardReviewVariant, welcomeOfferExpiresAt]);

    const handleWelcomeOfferClick = () => {
        posthog.capture('welcome_offer_popup_clicked', { action: 'see_offer' });
        if (onCheckOffer) onCheckOffer(); else onClose();
    };
    const handleWelcomeOfferLater = () => {
        posthog.capture('welcome_offer_popup_clicked', { action: 'continue_tasks' });
        onClose();
    };

    // --- REVIEW HANDLERS ---
    const handleStandardReviewClick = () => {
        if (reviewFiredRef.current) return;
        reviewFiredRef.current = true;
        posthog.capture('review_cta_clicked', {
            review_variant: standardReviewVariant,
            streak_day: streakDay,
            completion_level: completionLevel,
            time_on_popup_sec: Math.round((Date.now() - popupOpenedAtRef.current) / 100) / 10,
            platform: Capacitor.getPlatform(),
        });
        // Nie zamykamy popupu — natywny dialog pojawia się na wierzchu.
        // User wraca i zamyka popup klikając w tło lub "Nie teraz".
        // .then: Android = dialog zamknięty (shown+dismissed), iOS = call wykonany (może być cicho zignorowany)
        triggerInAppReview()
            .then(() => {
                posthog.capture('review_dialog_resolved', {
                    review_variant: standardReviewVariant,
                    streak_day: streakDay,
                    platform: Capacitor.getPlatform(),
                });
            })
            .catch(console.warn);
    };

    const handleStandardDismiss = () => {
        posthog.capture('review_dismissed', {
            review_variant: standardReviewVariant,
            streak_day: streakDay,
            completion_level: completionLevel,
            time_on_popup_sec: Math.round((Date.now() - popupOpenedAtRef.current) / 100) / 10,
            platform: Capacitor.getPlatform(),
            dismissed_via: 'button',
        });
        onClose();
    };

    const handleBackdropClick = () => {
        if (shouldRequestReview && streakDay < 10) {
            if (reviewFiredRef.current) {
                // Review w toku — nie zamykaj, nie interferuj z natywnym dialogiem
                return;
            }
            // Przypadkowe tapnięcie tła zamiast przycisku — logujemy żeby śledzić
            posthog.capture('review_dismissed', {
                review_variant: standardReviewVariant,
                streak_day: streakDay,
                completion_level: completionLevel,
                time_on_popup_sec: Math.round((Date.now() - popupOpenedAtRef.current) / 100) / 10,
                platform: Capacitor.getPlatform(),
                dismissed_via: 'backdrop',
            });
        }
        onClose();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target) && !event.target.closest('button[aria-label="Informacje o Zamrożeniach Sesji"]')) {
                setShowTooltip(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const { icon, title, message, buttonText, buttonAction, buttonColor, secondaryButtonText, secondaryButtonAction, showGoalBar } = useMemo(() => {
        const iconClasses = "w-28 h-28";

        const tooltipTrigger = (
            <span ref={tooltipRef} className="relative inline-block align-middle ml-1">
                <button
                    type="button"
                    aria-label="Informacje o Zamrożeniach Sesji"
                    onClick={(e) => { e.stopPropagation(); setShowTooltip((prev) => !prev); }}
                    className="flex items-center justify-center text-gray-500 hover:text-gray-800"
                >
                    <FiHelpCircle className="h-5 w-5" />
                </button>
                {showTooltip && <FreezeInfoTooltip />}
            </span>
        );

        // ============================================================
        // 1. PRIORYTET: NAGRODA ZA PIERWSZY CEL — OFERTA POWITALNA
        // Prośba o review jej nie zasłoni: ta idzie dopiero po zakupie.
        // ============================================================
        if (welcomeOfferExpiresAt && completionLevel === 1) {
            const prices = getFormattedPrices();
            const ctx = welcomeOfferContext || {};
            const isDual = !!ctx.declares_pr;
            // Cel z onboardingu: przy rozszerzeniu cel z rozszerzenia; przy 30% bez liczby.
            const goalText = isDual
                ? ({ counts: '30%+ z rozszerzenia', good: '60%+ z rozszerzenia', top: '80%+ z rozszerzenia' })[ctx.goal_id_pr]
                : ({ pass: 'zdanej matury', solid: '60% na maturze', max: '90%+ na maturze' })[ctx.goal_id];
            const hoursLeft = Math.max(1, Math.round((welcomeOfferExpiresAt - Date.now() / 1000) / 3600));
            return {
                icon: <img src={OrzelGwiazdyIcon} alt="Orzeł z pucharem" className="w-24 h-24" />,
                title: "PIERWSZY KROK ZA TOBĄ",
                message: (
                    <div className="flex flex-col items-center w-full">
                        {goalText && (
                            <p className="text-center text-sm leading-relaxed mb-4">
                                Tak zaczyna się droga do <b>{goalText}</b>.
                            </p>
                        )}
                        <div className="w-full bg-white p-4 rounded-2xl border-2 border-[#E09F2E] text-center">
                            <p className="text-xs font-bold uppercase tracking-wider text-[#E09F2E] mb-1">
                                Nagroda: cena dla ambitnych
                            </p>
                            <p className="text-xs text-gray-500 mb-1">
                                {isDual ? 'Pakiet Podstawa + Rozszerzenie' : 'Matura podstawowa'}
                            </p>
                            <div className="flex items-baseline justify-center gap-3">
                                <span className="text-base text-gray-400 line-through">
                                    {isDual ? prices.matura_pp_pr_2027_regular : prices.matura_pp_2027_regular}
                                </span>
                                <span className="text-3xl font-bold text-[#233855]">
                                    {isDual ? prices.matura_pp_pr_2027_promo : prices.matura_pp_2027_promo}
                                </span>
                            </div>
                            <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#233855] mt-2">
                                <FiClock className="w-3.5 h-3.5" />
                                Ważna do jutra, 23:59 · zostało ok. {hoursLeft} h
                            </p>
                        </div>
                    </div>
                ),
                buttonText: "ZOBACZ OFERTĘ",
                buttonAction: handleWelcomeOfferClick,
                buttonColor: "bg-[#E09F2E] hover:bg-amber-500 text-white",
                secondaryButtonText: "Robię dalej zadania",
                secondaryButtonAction: handleWelcomeOfferLater,
                showGoalBar: true
            };
        }

        // ============================================================
        // 2. PRIORYTET: PROŚBA O REVIEW
        // ============================================================
        if (shouldRequestReview) {

            // --- WETERANI (STREAK >= 10 DNI) ---
            if (streakDay >= 10) {
                const handleVeteranReviewClick = () => {
                    posthog.capture('review_cta_clicked', {
                        review_variant: 'veteran',
                        streak_day: streakDay,
                        completion_level: completionLevel,
                        time_on_popup_sec: Math.round((Date.now() - popupOpenedAtRef.current) / 100) / 10,
                        platform: Capacitor.getPlatform(),
                    });
                    const platform = Capacitor.getPlatform();
                    if (platform === 'ios') {
                        // itms-apps:// otwiera App Store bezpośrednio, bez przejścia przez Safari
                        window.open('itms-apps://itunes.apple.com/app/id6754283687?action=write-review', '_system');
                    } else if (platform === 'android') {
                        window.open('market://details?id=matematly.pl', '_system');
                    }
                    onClose();
                };
                const handleVeteranDismiss = () => {
                    posthog.capture('review_dismissed', {
                        review_variant: 'veteran',
                        streak_day: streakDay,
                        completion_level: completionLevel,
                        time_on_popup_sec: Math.round((Date.now() - popupOpenedAtRef.current) / 100) / 10,
                        platform: Capacitor.getPlatform(),
                    });
                    onClose();
                };
                return {
                    icon: null,
                    title: `ZROBILIŚMY TO. ${streakDay} DNI.`,
                    message: (
                        <div className="text-left space-y-4 font-normal text-sm text-gray-800 leading-relaxed px-2">
                            <p>Z tej strony Patryk, twórca Matematly. Mało kto tu dociera. Robisz zadania już kilkanaście godzin.</p>
                            <p>Jeśli Matematly pomaga Ci z matmą i chcesz pomóc innym maturzystom - napisz proszę dłuższą opinię w App Store/Sklepie Play. </p>
                            <p>  Twoja jedna, długa recenzja niesamowicie wiele dla mnie znaczy!</p>
                            <p className="font-bold pt-2">Dzięki za zaufanie i powodzenia na maturze,<br/>Patryk z Matematly</p>
                        </div>
                    ),
                    buttonText: `Piszę recenzję dla twórcy ✍️`,
                    buttonAction: handleVeteranReviewClick,
                    buttonColor: "bg-[#111111] hover:bg-black text-white shadow-xl border border-gray-800",
                    secondaryButtonText: "Zamykam",
                    secondaryButtonAction: handleVeteranDismiss
                };
            }

            // --- WARIANT: STARS_ANON (personalizacja + info o sklepie + anonimowość) ---
            if (standardReviewVariant === 'stars_anon') {
                const storeName = Capacitor.getPlatform() === 'ios' ? 'App Store' : 'Sklepie Play';
                return {
                    icon: <img src={OrzelGwiazdyIcon} alt="Orzeł z gwiazdami" className={iconClasses} />,
                    title: "CEL ZALICZONY! 🎯",
                    message: (
                        <>
                            Ktoś właśnie szuka apki do matury i nie wie, czy może nam zaufać
                            {streakDay >= 2
                                ? <> — Ty masz <b>{streakDay} dni serii</b> i już wiesz.</>
                                : <> — Ty <b>już wiesz</b>, jak to działa.</>}
                            <br/><br/>
                            Twoja ocena w <b>{storeName}</b> (sama ocena bez komentarza jest anonimowa)
                            może pomóc ogarnąć matmę innym.
                        </>
                    ),
                    buttonText: "Oceniam w 5 sekund",
                    buttonAction: handleStandardReviewClick,
                    buttonColor: "bg-[#233855] hover:bg-[#1a2b42] text-white shadow-lg",
                    secondaryButtonText: "Teraz nie",
                    secondaryButtonAction: handleStandardDismiss
                };
            }

            // --- WARIANT: STARS (samo ocenianie, bez prośby o tekst) ---
            if (standardReviewVariant === 'stars') {
                return {
                    icon: <img src={OrzelGwiazdyIcon} alt="Orzeł z gwiazdami" className={iconClasses} />,
                    title: "CEL ZALICZONY! 🎯",
                    message: (
                        <>
                            Matematly to mała, polska apka — każda ocena w sklepie
                            pomaga kolejnym maturzystom nas znaleźć.
                            <br/><br/>
                            Nie musisz nic pisać - wystarczy <b>sama ocena</b> — zajmie Ci to tylko 5 sekund a dla nas zmienia wszystko!
                        </>
                    ),
                    buttonText: "Oceniam w 5 sekund",
                    buttonAction: handleStandardReviewClick,
                    buttonColor: "bg-[#233855] hover:bg-[#1a2b42] text-white shadow-lg",
                    secondaryButtonText: "Nie teraz",
                    secondaryButtonAction: handleStandardDismiss
                };
            }

            // --- WARIANT: DIRECT (baza — prośba o krótką recenzję) ---
            return {
                icon: <img src={OrzelGwiazdyIcon} alt="Orzeł z gwiazdami" className={iconClasses} />,
                title: "CEL ZALICZONY! 🎯",
                message: (
                    <>
                        Matematly to mała, polska apka - każda ocena w sklepie
                        pomaga kolejnym maturzystom nas znaleźć.
                        <br/><br/>
                        Napisz krótko <b>z czego się uczysz i czy widzisz
                        postępy</b> - to zajmie 30 sekund, a dla nas zmienia wszystko.
                    </>
                ),
                buttonText: "Oceniam w 30 sekund",
                buttonAction: handleStandardReviewClick,
                buttonColor: "bg-[#233855] hover:bg-[#1a2b42] text-white shadow-lg",
                secondaryButtonText: "Nie teraz",
                secondaryButtonAction: handleStandardDismiss
            };
        }

        // ============================================================
        // 3. PRIORYTET: PIERWSZA SESJA
        // ============================================================
        if (isFirstSession && completionLevel === 1) {
            return {
                icon: <img src={OrzelZpucharemIcon} alt="Złoty orzeł z pucharem" className={iconClasses} />,
                title: "PIERWSZY SUKCES! 🏆",
                message: (
                    <>
                        To był kawał dobrej roboty.
                        <br /><br />
                        Kluczem do maturalnego sukcesu jest <b>regularność</b>. Ten najtrudniejszy, pierwszy krok masz już za sobą. Tak trzymaj!
                    </>
                ),
                buttonText: "SUPER!",
                buttonAction: onClose,
                buttonColor: "bg-[#233855] hover:bg-[#2c456b]",
                showGoalBar: true
            };
        }

        // ============================================================
        // 4. STANDARDOWE POZIOMY
        // ============================================================
        switch (completionLevel) {
            case 1:
                return {
                    icon: <img src={OrzelZpucharemIcon} alt="Złoty orzeł z pucharem" className={iconClasses} />,
                    title: "Cel Dzienny Osiągnięty!",
                    message: <>Realizując cel ponownie, zdobędziesz <b>Zamrożenie ❄️</b> – Twoje narzędzie do planowania dni wolnych.</>,
                    buttonText: "SUPER!",
                    buttonAction: onClose,
                    buttonColor: "bg-[#233855] hover:bg-[#2c456b]",
                    showGoalBar: true
                };
            case 2:
                return {
                    icon: <img src={OrzelZLodemIcon} alt="Złoty orzeł z lodem" className={iconClasses} />,
                    title: "Podwójny Cel! Otrzymujesz Nagrodę!",
                    message: <> W nagrodę za dodatkowy wysiłek na Twoim koncie ląduje <b>Zamrożenie ❄️</b>!</>,
                    buttonText: "SUPER!",
                    buttonAction: onClose,
                    buttonColor: "bg-[#233855] hover:bg-[#2c456b]",
                    showGoalBar: true
                };
            case 3:
                return {
                    icon: <img src={OrzelZLodemIcon} alt="Złoty orzeł z lodem" className={iconClasses} />,
                    title: "Potrójny Cel! Niesamowite!",
                    message: <>Jesteś nie do zatrzymania! Zdobywasz kolejne<b> Zamrożenie ❄️</b>!</>,
                    buttonText: "SUPER!",
                    buttonAction: onClose,
                    buttonColor: "bg-[#233855] hover:bg-[#2c456b]",
                    showGoalBar: true
                };
            default:
                return {
                    icon: <img src={OrzelZpucharemIcon} alt="Złoty orzeł" className={iconClasses} />,
                    title: 'Gratulacje!',
                    message: 'Dobra robota!',
                    buttonText: 'OK',
                    buttonAction: onClose,
                    buttonColor: 'bg-[#233855] hover:bg-[#2c456b]'
                };
        }
    }, [completionLevel, showTooltip, isFirstSession, onClose, shouldRequestReview, streakDay, standardReviewVariant, welcomeOfferExpiresAt, welcomeOfferContext, onCheckOffer]);

    if (!title) return null;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[9998] p-4 backdrop-blur-sm animate-fade-in" onClick={handleBackdropClick}>
            <div className="bg-white rounded-xl lg:rounded-2xl shadow-[0_8px_30px_-5px_rgba(35,56,85,0.15)] p-6 max-w-sm w-full text-center transform animate-swoosh-in border border-gray-100" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-center mb-4">
                    {icon}
                </div>
                <h2 className="text-2xl font-apotek tracking-wider text-[#233855] mb-3 uppercase font-bold">
                    {title}
                </h2>
                {showGoalBar && <GoalFillBar completionLevel={completionLevel} reduced={reduced} solved={goalSolved} goal={goalTotal} />}
                <div className="text-gray-600 mb-6 text-sm leading-relaxed">
                    {message}
                </div>
                <button
                    onClick={buttonAction}
                    className={`w-full font-apotek tracking-widest uppercase text-sm text-white font-bold py-3 px-4 rounded-xl shadow-2xl transform transition-transform active:scale-95 ${buttonColor}`}
                >
                    {buttonText}
                </button>
                {secondaryButtonText && (
                    <button
                        onClick={secondaryButtonAction}
                        className="mt-4 w-full text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-wider py-2"
                    >
                        {secondaryButtonText}
                    </button>
                )}
            </div>
        </div>,
        document.body
    );
};

export default DailyGoalSuccessPopup;
